import { useMemo } from 'react';
import {
  Popper,
  Paper,
  ClickAwayListener,
  Stack,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

export const IssuePopover = ({
  selectedAnchorEl,
  popoverOpen,
  selectedIssue,
  closePopover,
  busy,
  popperRef,
  acceptIssue,
  dismissIssue,
  revertIssue,
  changeSelection,
  openIssues,
  acceptedIssues,
}) => {
  const virtualAnchor = useMemo(() => {
    if (!selectedAnchorEl) return null;
    return {
      getBoundingClientRect: () => selectedAnchorEl.getBoundingClientRect(),
    };
  }, [selectedAnchorEl]);

  const isOpen = popoverOpen && !!selectedIssue && !!virtualAnchor;

  return (
    <Popper
      open={isOpen}
      anchorEl={virtualAnchor}
      placement="bottom-start"
      popperRef={popperRef}
      modifiers={[
        { name: 'offset', options: { offset: [0, 6] } },
        { name: 'preventOverflow', options: { padding: 12, altAxis: true } },
        { name: 'flip', options: { padding: 12 } },
      ]}
      sx={{ zIndex: 1300, maxWidth: '100vw' }}
    >
      <ClickAwayListener onClickAway={closePopover}>
        <Paper
          elevation={8}
          sx={{
            // ✅ responsive sizing
            width: { xs: 'calc(100vw - 24px)', sm: 440 },
            maxWidth: 440,

            // ✅ keep it usable on short viewports
            maxHeight: 'min(70vh, 520px)',
            display: 'flex',
            flexDirection: 'column',

            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, py: 1.25, flex: '0 0 auto' }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 900,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {selectedIssue?.rule || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedIssue?.status === 'open'
                    ? 'Suggestion'
                    : selectedIssue?.status === 'accepted'
                      ? 'Applied change'
                      : ''}
                </Typography>
              </Box>

              <IconButton aria-label="close" onClick={closePopover} size="small">
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
            </Stack>
          </Box>

          <Divider />

          {/* Body (scrollable) */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              flex: '1 1 auto',
              overflowY: 'auto',
            }}
          >
            {selectedIssue?.status === 'open' && (
              <>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedIssue.message}
                </Typography>

                <Box sx={{ mt: 1.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 900, opacity: 0.8 }}>
                    Replace
                  </Typography>

                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedIssue.original}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 900, opacity: 0.8, mt: 1, display: 'block' }}
                  >
                    With
                  </Typography>

                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedIssue.suggested}
                  </Typography>
                </Box>
              </>
            )}

            {selectedIssue?.status === 'accepted' && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 900, opacity: 0.8 }}>
                  Original:
                </Typography>

                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {selectedIssue.original}
                </Typography>

                <Typography variant="caption" sx={{ fontWeight: 900, opacity: 0.8 }}>
                  Suggestion Inserted:
                </Typography>

                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {selectedIssue.suggested}
                </Typography>
              </>
            )}
          </Box>

          <Divider />

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1,
            }}
          >
            {selectedIssue?.status === 'open' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  className="myOdu__button primary"
                  onClick={() => acceptIssue(selectedIssue.id)}
                  disabled={busy}
                >
                  Insert Change
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  className="myOdu__button secondary"
                  onClick={() => dismissIssue(selectedIssue.id)}
                  disabled={busy}
                >
                  Dismiss Suggestion
                </Button>
              </Stack>
            ) : (
              <Button
                fullWidth
                variant="contained"
                className="myOdu__button primary"
                onClick={() => revertIssue(selectedIssue.id)}
                disabled={busy}
              >
                Revert to Original
              </Button>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: { xs: 'flex-end', sm: 'flex-end' },
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  selectedIssue?.status === 'open'
                    ? changeSelection(openIssues, 'prev')
                    : changeSelection(acceptedIssues, 'prev')
                }
                disabled={busy}
                aria-label="previous"
              >
                ‹
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  selectedIssue?.status === 'open'
                    ? changeSelection(openIssues, 'next')
                    : changeSelection(acceptedIssues, 'next')
                }
                disabled={busy}
                aria-label="next"
              >
                ›
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
